import './index.css'
import { type ReactElement, type ReactNode, useCallback } from 'react'

import React, { Suspense, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import * as Sentry from '@sentry/react'
import { registerSW } from 'virtual:pwa-register'

import { loadStartupStylesheets, StaticModules, STATIC_MODULES } from './store/StaticModules'
import { _isWebview } from './util/Util'
import StartupLoading from './StartupLoading'
import { PWA } from './util/pwa'
import { _composeString } from './util/StringUtil'
import { sentryBeforeSend } from './constant/SentryIgnore'

if (import.meta.env.VITE_NODE_ENV !== 'development' && import.meta.env.VITE_SENTRY_MAIN === '1') {
  console.log('Initializing Sentry with environment:', import.meta.env.VITE_NODE_ENV)
  Sentry.init({
    dsn: 'https://21ec23fdd6df8af351bb2fb95a3ee0a9@o4508351139217408.ingest.us.sentry.io/4508351142363136',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    environment: import.meta.env.VITE_NODE_ENV || 'production',
    beforeSend: sentryBeforeSend,
  })
}

let MainSuspense: (() => ReactElement) | null = null
if (import.meta.env.VITE_ENTRY === 'main') {
  const MainSuspenseCpn = () => {
    const [percent, setPercent] = useState(0)

    // prevent progress bar from going backward
    const guardedSetPercent = useCallback((percent: number) => {
      setPercent((p) => {
        return Math.max(p, percent)
      })
    }, [])

    window.setInitialProgressPercent = guardedSetPercent

    useEffect(() => {
      StartupLoading.tick()
      let current = 35
      window.setInitialProgressPercent(current)
      // at this point total load time can be guessed
      const totalTime = StartupLoading.lastTime * 100 / 35
      const percentPerSec = 100 / (totalTime / 1000)
      // console.log('guessed time', totalTime, percentPerSec)
      const interval = setInterval(() => {
        current += percentPerSec
        window.setInitialProgressPercent(Math.min(current, 95))
      }, 1000)

      return () => {
        clearInterval(interval)
      }
    }, [])

    return (
      <div className='full-page-loading' onContextMenu={(e) => e.preventDefault()}>
        <img className='loading-logo' src='/logo_full.svg' />
        <div className='sk-folding-cube'>
          <div className='sk-cube1 sk-cube' />
          <div className='sk-cube2 sk-cube' />
          <div className='sk-cube4 sk-cube' />
          <div className='sk-cube3 sk-cube' />
        </div>
        <div className='init-progress'>
          <div id='initial-load' className='init-progress-bar' style={{ width: `${percent}%` }}></div>
        </div>
      </div>
    )
  }
  MainSuspense = MainSuspenseCpn
}

const renderApp = async () => {
  let suspense: ReactNode = 'Loading...'
  if (MainSuspense) {
    suspense = <MainSuspense />
  }

  StartupLoading.tick()
  window.setInitialProgressPercent(20)

  /*========================================================================================*/
  /*=                                   APPLICATION MODE                                   =*/
  /*========================================================================================*/

  // load stylesheet
  await loadStartupStylesheets()

  // load modules
  await Promise.all([
    // StaticModules.load(STATIC_MODULES.JQUERY.id),
    StaticModules.load(STATIC_MODULES.POPPER.id),
  ])

  StartupLoading.tick()
  window.setInitialProgressPercent(25)

  await Promise.all([
    StaticModules.load(STATIC_MODULES.BOOTSTRAP.id),
    // StaticModules.load(STATIC_MODULES.POLYFILL.id),
    StaticModules.load(STATIC_MODULES.PACKER.id),
    StaticModules.load(STATIC_MODULES.PMK_VIEWER.id),
    StaticModules.load(STATIC_MODULES.GIF.id),
  ])

  StartupLoading.tick()
  window.setInitialProgressPercent(30)

  // custom authentication mechanism
  if (import.meta.env.VITE_USE_COOKIE_LOGIN === '1') {
    const { loginFromCookie } = await import('./util/FirebaseAuthUtil')
    await loginFromCookie()
  }

  // i18n
  const { i18nReady, initI18n } = await import('./util/i18n')
  initI18n((lang) => {
    // the installed apps have no service worker to tell
    if (import.meta.env.VITE_PLATFORM !== 'web') {
      return
    }
    try {
      PWA.sendLanguageCode(lang)
    } catch (e) {
      console.warn('Failed to send language to service worker', e)
    }
  })
  await i18nReady

  // installed apps only: renderer-side adjustments (dynamic import keeps them out of the web bundle)
  if (import.meta.env.VITE_PLATFORM !== 'web') {
    const { installDesktopRuntime } = await import('./util/DesktopRuntime')
    installDesktopRuntime()
  }

  // import App here to prevent Firebase being initialized before custom authen complete
  const Main = React.lazy(() => import('./App'))

  // render
  ReactDOM.render(
    <React.StrictMode>
      <Suspense fallback={suspense}>
        <Main />
      </Suspense>
    </React.StrictMode>,
    document.getElementById('root')
  )

  if (import.meta.env.VITE_ENTRY === 'main' && import.meta.env.VITE_USE_SERVICE_WORKER === '1') {
    // delete old & wrongfully cached files
    caches.open('publicFiles').then((cache) => {
      cache.keys().then((keyList) => {
        keyList.forEach((req) => {
          if (req.url.includes('/__/auth/')) {
            cache.delete(req)
          }
        })
      })
    })

    // register service worker
    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        PWA.onUpdate(updateSW)
      },
      onOfflineReady() {
        console.log('Service worker is ready for offline use')
      },
      onRegistered(registration) {
        PWA.setRegistration(registration)
      },
    })
  }
}

// Test mode: ?test in URL renders a blank diagnostic page with no app modules loaded
if (new URLSearchParams(location.search).has('test')) {
  import('./page/SafeModePage/SafeModePage').then(({ default: SafeModePage }) => {
    ReactDOM.render(
      <React.StrictMode>
        <SafeModePage />
      </React.StrictMode>,
      document.getElementById('root')
    )
  })
} else {
  // Basic authentication for staging
  let ok = true
  // the gate only guards the public staging URL (and Electron has no window.prompt())
  if (import.meta.env.VITE_NODE_ENV === 'staging' && import.meta.env.VITE_PLATFORM === 'web') {
    const PWD = [50, 51, 53, 53, 54, 55]
    ok = localStorage.getItem('basic-authen') === _composeString(PWD)

    if (!ok) {
      const wv = _isWebview()
      // bypass password check for embedded browser of Facebook and Instagram
      if (!wv || !wv.app) {
        const password = prompt('Enter password: ')
        console.log('pwd', password)
        if (password === _composeString(PWD)) {
          ok = true
          localStorage.setItem('basic-authen', password)
        }
      }
    }
    console.log(ok ? 'password ok' : 'password invalid')
  }

  if (ok) {
    renderApp()
  }
}
